/** The English catalogue — the fallback, and the source every other locale is
 *  translated FROM. Adding a user-facing string means adding it here (and then
 *  translating it in es.ts / fr.ts; a missing translation falls back to
 *  English, a hardcoded string never does).
 *
 *  Keys are `area.thing`. Values may carry `{vars}` and, where a count is
 *  involved, a `|` separating singular from plural. Never build a sentence by
 *  concatenating fragments — word order is not universal and the fragments
 *  cannot be translated independently.
 */
export const EN: Record<string, string> = {
  // --- Language picker ---------------------------------------------------
  "locale.label": "Language",
  "locale.help": "Changes the language on this browser only. Your account default is set in Settings → Region.",

  // --- Common actions ----------------------------------------------------
  "action.save": "Save",
  "action.saving": "Saving…",
  "action.saved": "Saved",
  "action.cancel": "Cancel",

  // --- Settings: region --------------------------------------------------
  "settings.region.title": "Region & language",
  "settings.region.blurb": "Where you operate decides how we write dates and money, which units we show sizes in, and how we bill your NestPlot subscription. Everything below is a default your country sets — you can override any of it.",
  "settings.region.country": "Country",
  "settings.region.country.help": "Sets the defaults below and the currency we bill your subscription in. Changing it re-applies the defaults.",
  "settings.region.locale": "Default language",
  "settings.region.locale.help": "The language new staff seats and shared screens start in. Anyone can pick their own language for their own browser.",
  "settings.region.currency": "Currency",
  "settings.region.currency.help": "The currency you collect rent in. Your NestPlot subscription is billed in your country's currency.",
  "settings.region.timezone": "Time zone",
  "settings.region.timezone.help": "Digests, due dates and any 'local 7am' job follow this.",
  "settings.region.measurement": "Unit sizes",
  "settings.region.measurement.help": "How unit and space sizes are shown. Your inventory is stored the same way either way, so changing this is safe at any time.",
  "settings.region.measurement.imperial": "Square feet (sq ft) — US, Canada, UK",
  "settings.region.measurement.metric": "Square metres (m²) — Australia, New Zealand, Europe",
  "settings.region.notSellable": "We can't bill a NestPlot subscription in {country} yet — the core product (listings, online rent through your own Stripe, the tenant portal, access control) works, but our subscription billing, tax and the statutory lease/lien tools are set up for the US and English-speaking markets. Talk to us and we'll tell you where that stands.",

  // --- Legal framework card ---------------------------------------------
  "legal.title": "Your legal framework",
  "legal.privacy": "Data protection",
  "legal.regulator": "Regulator",
  "legal.tenancy": "Tenancy & storage law",
  "legal.agreement": "What we sign with you",
  "legal.statutoryEngine": "Lease & lien automation",
  "legal.footer": "We keep the records your obligations rest on — who rented each unit, under what terms, what was paid and when. We do NOT write any document you issue under your own name, and none of this is legal advice: your obligations are yours, and your own regulator is the authority on them.",

  // --- Tax note (on pricing + billing) ----------------------------------
  "tax.note": "Prices exclude {label}, which is added at checkout based on your billing address.",
  "tax.reverseCharge": "If your business is {label}-registered, add your {label} number at checkout — the supply is then reverse-charged and we collect no {label}.",

  // --- Pricing page ------------------------------------------------------
  "pricing.eyebrow": "One flat price · every size · 0% of your customer rent",
  "pricing.title": "Priced for one property or one thousand.",
  "pricing.monthly": "Monthly",
  "pricing.annual": "Annual",
  "pricing.annualSave": "2 months free",
  "pricing.perMonth": "/mo",
  "pricing.billedAnnually": "{amount} billed annually",
  "pricing.monthlyCancel": "Monthly · cancel anytime",
  "pricing.contactUs": "Contact us",
  "pricing.startTrial": "Start free trial",
  "pricing.contactSales": "Contact sales",
  "pricing.mostOperators": "Most operators",
  "pricing.currencyLabel": "Prices shown in",

  // --- Navigation (operator) --------------------------------------------
  // Keyed here so the nav can speak the reader's language; an absent key
  // falls back to the English the registry already carries.
  "nav.today": "Today",
  "nav.properties": "Properties",
  "nav.tenants": "Tenants",
  "nav.leasing": "Leasing",
  "nav.leases": "Leases",
  "nav.maintenance": "Maintenance",
  "nav.reports": "Reports",
  "nav.billing": "Billing",
  "nav.settings": "Settings",
};
